import { apiClient, unwrap, type ApiSuccess } from '@/lib/api-client';

/**
 * Master API helpers. Mirrors the four master endpoints declared in
 * `docs/specs/06-ui-api-spec.md` §5:
 *   - /api/masters/incoming-items
 *   - /api/masters/materials
 *   - /api/masters/normalization-rules
 *   - /api/masters/destinations
 *
 * All endpoints follow the standard `{ data, meta }` envelope. `unwrap`
 * strips the envelope; list calls return `{ rows, meta }` so paginators
 * can read the total count.
 */

export type MasterKind = 'incoming-items' | 'materials' | 'normalization-rules' | 'destinations';

export type MasterExportKind =
  | 'incoming-items'
  | 'materials'
  | 'destinations'
  | 'package-types'
  | 'dismantling-methods'
  | 'warehouses'
  | 'suppliers';

/**
 * URL slug for the `PUT` (direct edit) and `POST .../propose-change`
 * endpoints. Backend uses `normalization` (no `-rules` suffix) for these
 * routes per the change-proposal contract.
 */
export type MasterEditKind =
  | 'incoming-items'
  | 'materials'
  | 'normalization-rules'
  | 'destinations';

export interface MasterListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface MasterListResult<T> {
  rows: T[];
  meta: MasterListMeta;
}

export interface MasterListParams {
  status?: 'pending' | 'approved' | 'rejected';
  active?: boolean;
  page?: number;
  per_page?: number;
  /** Normalization-rules only: filter by the targeted master type. */
  master_type?: 'incoming_item' | 'material';
}

const path = (kind: MasterKind, suffix = ''): string => `/masters/${kind}${suffix}`;

export async function listMaster<T>(
  kind: MasterKind,
  params: MasterListParams = {},
): Promise<MasterListResult<T>> {
  const res = await apiClient.get<ApiSuccess<T[], MasterListMeta>>(path(kind), { params });
  return {
    rows: res.data.data,
    meta: res.data.meta ?? { total: res.data.data.length, page: 1, per_page: res.data.data.length },
  };
}

export async function createMaster<T, TInput = Record<string, unknown>>(
  kind: MasterKind,
  body: TInput,
): Promise<T> {
  return unwrap(apiClient.post<ApiSuccess<T>>(path(kind), body));
}

export async function approveMaster<T>(kind: MasterKind, id: string, reason = ''): Promise<T> {
  return unwrap(apiClient.post<ApiSuccess<T>>(path(kind, `/${id}/approve`), { reason }));
}

export async function rejectMaster<T>(kind: MasterKind, id: string, reason = ''): Promise<T> {
  return unwrap(apiClient.post<ApiSuccess<T>>(path(kind, `/${id}/reject`), { reason }));
}

export async function retireMaster(kind: MasterKind, id: string): Promise<void> {
  await apiClient.delete(path(kind, `/${id}`));
}

export async function activateMaster(kind: MasterKind, id: string): Promise<void> {
  await apiClient.post(path(kind, `/${id}/activate`));
}

export async function purgeMaster(kind: MasterKind, id: string): Promise<void> {
  await apiClient.delete(path(kind, `/${id}/purge`));
}

/**
 * Direct edit (manager-only). `PUT /api/masters/<kind>/:id` with a
 * partial body. Returns the updated DTO. Backend gates this on
 * users with master-management permissions.
 */
export async function updateMasterDirect<T, TBody = Record<string, unknown>>(
  kind: MasterEditKind,
  id: string,
  body: TBody,
): Promise<T> {
  return unwrap(apiClient.put<ApiSuccess<T>>(`/masters/${kind}/${id}`, body));
}

/**
 * Submit a change-proposal (supervisor+ flow). Returns the created audit
 * approval row. `reason` is required by the backend.
 */
export interface ProposeChangeResult {
  audit_approval_id: string;
}

export async function proposeMasterChange<TBody = Record<string, unknown>>(
  kind: MasterEditKind,
  id: string,
  body: { proposed: TBody; reason: string },
): Promise<ProposeChangeResult> {
  return unwrap(
    apiClient.post<ApiSuccess<ProposeChangeResult>>(`/masters/${kind}/${id}/propose-change`, body),
  );
}

export interface MasterExportParams {
  status?: string;
  active?: boolean;
  active_only?: boolean;
  q?: string;
  facility_id?: string;
  company_id?: string;
}

function fallbackExportFileName(kind: MasterExportKind): string {
  return `efs-master-${kind}.xlsx`;
}

function contentDispositionFileName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return value.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export async function exportMasterWorkbook(
  kind: MasterExportKind,
  params: MasterExportParams = {},
): Promise<string> {
  const res = await apiClient.get<Blob>(`/master-exports/${kind}`, {
    params,
    responseType: 'blob',
  });
  const fileName =
    contentDispositionFileName(res.headers?.['content-disposition']) ??
    fallbackExportFileName(kind);
  downloadBlob(res.data, fileName);
  return fileName;
}
